import type { RouteObject } from 'react-router-dom';

import { erpScopedLazy } from './pages/workspace/erp-scope-boundary';

export const workbenchRoutes = [
  {
    path: '/chat',
    lazy: () => import('./pages/workspace/chat/index'),
  },
  {
    path: '/all',
    lazy: () => import('./pages/workspace/all-page/all-page'),
  },
  {
    path: '/collection',
    lazy: () => import('./pages/workspace/all-collection'),
  },
  {
    path: '/collection/:collectionId',
    lazy: () => import('./pages/workspace/collection/index'),
  },
  {
    path: '/tag',
    lazy: () => import('./pages/workspace/all-tag'),
  },
  {
    path: '/tag/:tagId',
    lazy: () => import('./pages/workspace/tag'),
  },
  {
    path: '/trash',
    lazy: () => import('./pages/workspace/trash-page'),
  },
  {
    path: '/records',
    lazy: erpScopedLazy(() => import('./pages/workspace/records/records')),
  },
  // IP2 Item Passport: ONE page showing an item's whole life. It lives UNDER `/records` on
  // purpose — `/records` is already an `ERP_ROUTE_PREFIXES` entry, so `isErpPath`, the info-tip
  // gating and `ErpScopeBoundary` all work with zero changes, and a top-level `/item/:id` would
  // collide with the `/:pageId` catch-all below.
  {
    path: '/records/:id',
    lazy: erpScopedLazy(
      () => import('./pages/workspace/records/item-passport')
    ),
  },
  {
    path: '/ops/worklist',
    lazy: erpScopedLazy(() => import('./pages/workspace/ops/worklist')),
  },
  {
    path: '/ops/dashboard',
    lazy: erpScopedLazy(() => import('./pages/workspace/ops/dashboard')),
  },
  {
    path: '/ops/intake',
    lazy: erpScopedLazy(() => import('./pages/workspace/ops/intake')),
  },
  {
    path: '/ops/equipment',
    lazy: erpScopedLazy(() => import('./pages/workspace/ops/equipment')),
  },
  // pipe (floor stations, Task 0): the five station screens. Each is nav-gated on the SERVER's own
  // `/me/provisioning` `stations` verdict (`rbac::may_work_station`) — see stations/station-registry.
  // The routes themselves are open like every ERP route: the Core API is the authority and refuses
  // the work, and each shell re-reads the same server verdict before rendering any affordance.
  {
    path: '/ops/wipe',
    lazy: erpScopedLazy(() => import('./pages/workspace/ops/wipe/wipe')),
  },
  {
    path: '/ops/qc',
    lazy: erpScopedLazy(() => import('./pages/workspace/ops/qc/qc')),
  },
  {
    path: '/ops/dispatch',
    lazy: erpScopedLazy(
      () => import('./pages/workspace/ops/dispatch/dispatch')
    ),
  },
  {
    path: '/ops/outbound',
    lazy: erpScopedLazy(
      () => import('./pages/workspace/ops/outbound/outbound')
    ),
  },
  {
    path: '/finance/invoices',
    lazy: erpScopedLazy(
      () => import('./pages/workspace/finance/invoices/invoices')
    ),
  },
  {
    // P4.7: manager/admin staff position map (server-gated to the StaffLocation grant).
    path: '/ops/position-map',
    lazy: erpScopedLazy(() => import('./pages/workspace/ops/position-map')),
  },
  {
    // P4.7: the employee's OWN location-sharing screen (consent + indicator + own pings).
    path: '/me/location',
    lazy: erpScopedLazy(() => import('./pages/workspace/me/my-location')),
  },
  {
    path: '/customers',
    lazy: erpScopedLazy(() => import('./pages/workspace/customers/customers')),
  },
  {
    path: '/customers/:accountId',
    lazy: erpScopedLazy(
      () => import('./pages/workspace/customers/customer-detail')
    ),
  },
  {
    path: '/sites',
    lazy: erpScopedLazy(() => import('./pages/workspace/sites/sites')),
  },
  // pipe (frontend wave FB): Refining / Comms / Agreements / Inbound — before the /:pageId catch-all.
  {
    path: '/refining/lots',
    lazy: erpScopedLazy(() => import('./pages/workspace/refining/lots')),
  },
  {
    path: '/refining/assays',
    lazy: erpScopedLazy(() => import('./pages/workspace/refining/assays')),
  },
  {
    path: '/refining/bars',
    lazy: erpScopedLazy(() => import('./pages/workspace/refining/bars')),
  },
  {
    path: '/comms/notifications',
    lazy: erpScopedLazy(() => import('./pages/workspace/comms/notifications')),
  },
  {
    path: '/comms/messages',
    lazy: erpScopedLazy(() => import('./pages/workspace/comms/messages')),
  },
  {
    path: '/comms/send',
    lazy: erpScopedLazy(() => import('./pages/workspace/comms/send')),
  },
  {
    path: '/comms/subscriptions',
    lazy: erpScopedLazy(() => import('./pages/workspace/comms/subscriptions')),
  },
  {
    path: '/comms/monday',
    lazy: erpScopedLazy(() => import('./pages/workspace/comms/monday')),
  },
  {
    path: '/comms/test-event',
    lazy: erpScopedLazy(() => import('./pages/workspace/comms/test-event')),
  },
  {
    path: '/agreements',
    lazy: erpScopedLazy(
      () => import('./pages/workspace/agreements/agreements')
    ),
  },
  {
    path: '/inbound',
    lazy: erpScopedLazy(() => import('./pages/workspace/inbound/inbound')),
  },
  {
    path: '/inbound/:orderId',
    lazy: erpScopedLazy(() => import('./pages/workspace/inbound/order-detail')),
  },
  {
    path: '/work/dashboard',
    lazy: erpScopedLazy(() => import('./pages/workspace/work/dashboard')),
  },
  {
    path: '/director',
    lazy: erpScopedLazy(() => import('./pages/workspace/director/director')),
  },
  {
    path: '/director/people/:personId',
    lazy: erpScopedLazy(() => import('./pages/workspace/director/director')),
  },
  {
    path: '/director/:id',
    lazy: erpScopedLazy(() => import('./pages/workspace/director/director')),
  },
  {
    path: '/work/boards',
    lazy: erpScopedLazy(() => import('./pages/workspace/work/boards')),
  },
  {
    path: '/work/boards/:boardId',
    lazy: erpScopedLazy(() => import('./pages/workspace/work/board-detail')),
  },
  {
    path: '/work/items',
    lazy: erpScopedLazy(() => import('./pages/workspace/work/items')),
  },
  {
    path: '/work/feedback',
    lazy: erpScopedLazy(() => import('./pages/workspace/work/feedback')),
  },
  {
    path: '/work/requests',
    lazy: erpScopedLazy(() => import('./pages/workspace/work/requests')),
  },
  {
    path: '/work/forms',
    lazy: erpScopedLazy(() => import('./pages/workspace/work/forms')),
  },
  {
    path: '/work/checklists',
    lazy: erpScopedLazy(() => import('./pages/workspace/work/checklists')),
  },
  {
    path: '/work/directory',
    lazy: erpScopedLazy(() => import('./pages/workspace/work/directory')),
  },
  {
    path: '/work/org-chart',
    lazy: erpScopedLazy(() => import('./pages/workspace/work/org-chart')),
  },
  {
    path: '/work/automations',
    lazy: erpScopedLazy(() => import('./pages/workspace/work/automations')),
  },
  {
    path: '/qms/dashboard',
    lazy: erpScopedLazy(() => import('./pages/workspace/qms/dashboard')),
  },
  {
    path: '/qms/training',
    lazy: erpScopedLazy(() => import('./pages/workspace/qms/training')),
  },
  {
    path: '/qms/calibration',
    lazy: erpScopedLazy(() => import('./pages/workspace/qms/calibration')),
  },
  {
    path: '/qms/documents',
    lazy: erpScopedLazy(() => import('./pages/workspace/qms/documents')),
  },
  {
    path: '/qms/ncr',
    lazy: erpScopedLazy(() => import('./pages/workspace/qms/ncr')),
  },
  {
    path: '/qms/audits',
    lazy: erpScopedLazy(() => import('./pages/workspace/qms/audits')),
  },
  {
    path: '/qms/test-protocols',
    lazy: erpScopedLazy(() => import('./pages/workspace/qms/test-protocols')),
  },
  {
    path: '/qms/reviews',
    lazy: erpScopedLazy(() => import('./pages/workspace/qms/reviews')),
  },
  // pipe (frontend wave F5): Sales + Listings — before the /:pageId catch-all.
  {
    path: '/sales/leads',
    lazy: erpScopedLazy(() => import('./pages/workspace/sales/leads')),
  },
  {
    path: '/sales/quotes',
    lazy: erpScopedLazy(() => import('./pages/workspace/sales/quotes')),
  },
  {
    path: '/sales/bids',
    lazy: erpScopedLazy(() => import('./pages/workspace/sales/bids')),
  },
  {
    path: '/sales/quote-requests',
    lazy: erpScopedLazy(() => import('./pages/workspace/sales/quote-requests')),
  },
  {
    path: '/sales/pipeline',
    lazy: erpScopedLazy(() => import('./pages/workspace/sales/pipeline')),
  },
  {
    path: '/sales/campaigns',
    lazy: erpScopedLazy(() => import('./pages/workspace/sales/campaigns')),
  },
  {
    path: '/sales/activities',
    lazy: erpScopedLazy(() => import('./pages/workspace/sales/activities')),
  },
  // pipe (ROMS+Monday P4.3): Customer POs — the po_discrepancies commitment roster.
  {
    path: '/sales/pos',
    lazy: erpScopedLazy(() => import('./pages/workspace/sales/pos')),
  },
  {
    path: '/listings',
    lazy: erpScopedLazy(() => import('./pages/workspace/listings/listings')),
  },
  {
    path: '/listings/orders',
    lazy: erpScopedLazy(() => import('./pages/workspace/listings/orders')),
  },
  // pipe (ROMS+Monday P0.1): the Reports screen — before the /:pageId catch-all.
  {
    path: '/reports',
    lazy: erpScopedLazy(() => import('./pages/workspace/reports/reports')),
  },
  // pipe (ask-PIPE): the read-only natural-language query screen — before the /:pageId catch-all.
  {
    path: '/ask',
    lazy: erpScopedLazy(() => import('./pages/workspace/ask/ask')),
  },
  {
    path: '/:pageId',
    lazy: () => import('./pages/workspace/detail-page/detail-page'),
  },
  {
    path: '/:pageId/attachments/:attachmentId',
    lazy: () => import('./pages/workspace/attachment/index'),
  },
  {
    path: '/journals',
    lazy: () => import('./pages/workspace/journals'),
  },
  {
    path: '/settings',
    lazy: () => import('./pages/workspace/settings'),
  },
  {
    path: '*',
    lazy: () => import('./pages/404'),
  },
] satisfies RouteObject[];
